/**
 * Market Context Widget Utilities
 *
 * MECE Regime Classification System for Covered Call Strategy
 */

export type RegimeType = 'FAIR_WEATHER' | 'STORMY_GROWTH' | 'DISTRIBUTION' | 'STAGNATION' | 'TOXIC';

export interface RegimeInfo {
  type: RegimeType;
  label: string;
  emoji: string;
  color: string;
  description: string;
}

export interface MarketInputs {
  vix: number;
  priceAboveSma: boolean;
  percentAboveSma: number;
  slopeDirection: 'up' | 'down' | 'flat' | null;  // null = unknown (not enough SPY history)
}

/**
 * MECE Regime Classification
 *
 * Evaluation Order: TOXIC > STAGNATION > FAIR_WEATHER/STORMY_GROWTH/DISTRIBUTION
 *
 * | Regime        | Criteria                                     | Color   |
 * |---------------|----------------------------------------------|---------|
 * | FAIR_WEATHER  | VIX < 20 AND Price > SMA AND Slope > 0       | #10B981 |
 * | STORMY_GROWTH | VIX >= 20 AND Price > SMA AND Slope > 0      | #A855F7 |
 * | DISTRIBUTION  | VIX < 20 AND Price > SMA AND Slope < 0       | #F59E0B |
 * | STAGNATION    | Price within +/-2% of SMA                    | #10B981 |
 * | TOXIC         | Price < SMA (Slope irrelevant)               | #EF4444 |
 */
export function calculateRegime(inputs: MarketInputs): RegimeInfo {
  const { vix, priceAboveSma, percentAboveSma, slopeDirection } = inputs;

  // Check TOXIC first (Price below SMA-200)
  if (!priceAboveSma) {
    return {
      type: 'TOXIC',
      label: 'Toxic',
      emoji: '☠️',
      color: '#EF4444',
      description: 'Historical data suggests defensive positioning when price is below the 200-day moving average.',
    };
  }

  // Check STAGNATION (within +/-2% of SMA)
  if (Math.abs(percentAboveSma) <= 2) {
    return {
      type: 'STAGNATION',
      label: 'Stagnation',
      emoji: '😐',
      color: '#10B981',
      description: 'Historical data suggests focusing on high-liquidity names when market is range-bound near the 200-day average.',
    };
  }

  // Price is above SMA (and not in stagnation zone)
  const highVix = vix >= 20;
  const slopeUp = slopeDirection === 'up';
  const slopeDown = slopeDirection === 'down';

  // FAIR_WEATHER: VIX < 20 AND Price > SMA AND Slope > 0
  if (!highVix && slopeUp) {
    return {
      type: 'FAIR_WEATHER',
      label: 'Fair Weather',
      emoji: '☀️',
      color: '#10B981',
      description: 'Historical data suggests this is an optimal environment for covered call writing with balanced risk/reward.',
    };
  }

  // STORMY_GROWTH: VIX >= 20 AND Price > SMA AND Slope > 0
  if (highVix && slopeUp) {
    return {
      type: 'STORMY_GROWTH',
      label: 'Stormy Growth',
      emoji: '⛈️',
      color: '#A855F7',
      description: 'Historical data suggests elevated premiums available but with increased volatility. Consider wider strikes.',
    };
  }

  // DISTRIBUTION: VIX < 20 AND Price > SMA AND Slope < 0
  if (!highVix && slopeDown) {
    return {
      type: 'DISTRIBUTION',
      label: 'Distribution',
      emoji: '⚠️',
      color: '#F59E0B',
      description: 'Historical data suggests the uptrend may be weakening. Consider value-oriented names with dividends.',
    };
  }

  // Edge case: VIX >= 20 AND Slope down (or flat) - treat as STORMY_GROWTH
  if (highVix) {
    return {
      type: 'STORMY_GROWTH',
      label: 'Stormy Growth',
      emoji: '⛈️',
      color: '#A855F7',
      description: 'Historical data suggests elevated premiums available but with increased volatility. Consider wider strikes.',
    };
  }

  // Default fallback (VIX < 20, Slope flat, Price > SMA)
  return {
    type: 'FAIR_WEATHER',
    label: 'Fair Weather',
    emoji: '☀️',
    color: '#10B981',
    description: 'Historical data suggests this is an optimal environment for covered call writing with balanced risk/reward.',
  };
}

/**
 * Get VIX zone for gauge coloring
 * Green: 0-15, Gray: 15-24, Red: 25+
 */
export function getVixZone(vix: number): 'green' | 'gray' | 'red' {
  if (vix < 15) return 'green';
  if (vix < 25) return 'gray';
  return 'red';
}

/**
 * Calculate needle rotation for VIX gauge
 * Scale: 0-40 VIX maps to -90 to +90 degrees
 */
export function getVixNeedleRotation(vix: number): number {
  const clampedVix = Math.max(0, Math.min(40, vix));
  // Map 0-40 to -90 to +90 degrees
  return (clampedVix / 40) * 180 - 90;
}
